import { useCallback, useEffect, useMemo, useState } from 'react';
import GridTable from '../../table/GridTable';
import { ColDef } from 'ag-grid-community';
import { useFortuneLabCodeStore, IFortuneLabCode } from '../../../store/useFortuneLabCode';
import CodeModal from './CodeModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faLink, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { preventWheelChange } from '../../../util/numberInput';

const SHARE_LINK_BASE =
  process.env.REACT_APP_FORTUNELAB_SHARE_LINK_BASE ??
  'https://today-saju.coredev.co.kr/fortune-lab/';

const SCRIPT_URL =
    process.env.REACT_APP_EMBED_SCRIPT_URL ??
    'https://saju.coredev.co.kr/saju-embed.js';

const BUTTON_SCRIPT_URL =
  process.env.REACT_APP_EMBED_BUTTON_SCRIPT_URL ??
  'https://saju.coredev.co.kr/saju-embed-button.js';

const CodeManagementPage = () => {
  const { codeList, codePage, fetchCodes, loading } = useFortuneLabCodeStore();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<IFortuneLabCode | null>(null);

  // 임베드 코드 모달을 위한 상태
  const [isEmbedModalOpen, setEmbedModalOpen] = useState(false);
  const [embedCodeToCopy, setEmbedCodeToCopy] = useState('');

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handlePagination = useCallback(
    (page: number) => {
      fetchCodes(page);
    },
    [fetchCodes]
  );

  const copyShareLink = useCallback((trkId: string) => {
    const shareLink = `${SHARE_LINK_BASE}${trkId}`;
    navigator.clipboard.writeText(shareLink).then(
      () => toast.success('공유 링크가 복사되었습니다.'),
      () => toast.error('복사에 실패했습니다.'),
    );
  }, []);

  const [embedType, setEmbedType] = useState<'landing' | 'button'>('landing');
  const [landingWidth, setLandingWidth] = useState('600');
  const [landingOnlyForm, setLandingOnlyForm] = useState(false);
  const [buttonWidth, setButtonWidth] = useState('600');
  const [buttonModalPosition, setButtonModalPosition] = useState<'left' | 'center' | 'right'>('center');
  const [buttonTypingEnabled, setButtonTypingEnabled] = useState(true);

  const buildLandingEmbedCode = useCallback(
    (trkId: string, width: string, onlyForm: boolean) =>
      `<saju-embed landing="fortune-lab/${trkId}" width="${width}"${onlyForm ? ' onlyform="1"' : ''}></saju-embed>\n<script src="${SCRIPT_URL}"></script>`,
    [SCRIPT_URL]
  );

  const buildButtonEmbedCode = useCallback(
    (trkId: string, width: string, modalPosition: 'left' | 'center' | 'right', typingEnabled: boolean) => {
      const typingAttribute = typingEnabled ? '' : ' typing-enabled="0"';
      return `<saju-embed-button landing="fortune-lab/${trkId}" width="${width}" modal-position="${modalPosition}"${typingAttribute}></saju-embed-button>\n<script src="${BUTTON_SCRIPT_URL}"></script>`;
    },
    [BUTTON_SCRIPT_URL]
  );

  const resetEmbedModalState = useCallback(() => {
    setEmbedModalOpen(false);
    setEmbedCodeToCopy('');
    setEmbedType('landing');
    setLandingWidth('600');
    setLandingOnlyForm(false);
    setButtonWidth('600');
    setButtonModalPosition('center');
    setButtonTypingEnabled(true);
    setSelectedCode(null);
  }, []);

  const handleOpenEmbedModal = useCallback((code: IFortuneLabCode) => {
    setSelectedCode(code);
    setEmbedType('landing');
    setLandingWidth('600');
    setLandingOnlyForm(false);
    setButtonWidth('600');
    setButtonModalPosition('center');
    setButtonTypingEnabled(true);
    setEmbedCodeToCopy('');
    setEmbedModalOpen(true);
  }, []);

  const handleCopyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(embedCodeToCopy).then(
      () => {
        toast.success('임베드 코드가 복사되었습니다.');
        resetEmbedModalState();
      },
      () => toast.error('복사에 실패했습니다.'),
    );
  }, [embedCodeToCopy, resetEmbedModalState]);

  const copyCouponLink = useCallback((slug: string) => {
    const normalized = slug.startsWith('http') ? slug : `https://${slug}`;
    navigator.clipboard.writeText(normalized).then(
      () => toast.success('쿠폰 링크가 복사되었습니다.'),
      () => toast.error('복사에 실패했습니다.'),
    );
  }, []);

	  const columnDefs = useMemo<ColDef<IFortuneLabCode>[]>(
	    () => [
      // ... 다른 열 정의는 그대로 ...
      {
        headerName: '캠페인명',
        field: 'campaignName',
        flex: 2,
        valueFormatter: params => params.value ?? '-',
      },
      {
        headerName: '공유 링크',
        flex: 3,
        cellRenderer: (params: any) => {
          const trkId = params.data?.trkId;
          if (!trkId) return null;
          const link = `${SHARE_LINK_BASE}${trkId}`;
          return (
            <div className="!flex !items-center !gap-2 !text-sm !h-full !w-full" style={{ minWidth: 0 }}>
              <span className="!flex-1 !truncate" title={link} style={{ minWidth: 0 }}>
                {link}
              </span>
              <button
                className="!text-blue-600 hover:!text-blue-800"
                onClick={() => copyShareLink(trkId)}
                title="공유 링크 복사"
              >
                <FontAwesomeIcon icon={faLink} />
              </button>
            </div>
          );
        },
      },
      {
        headerName: '쿠폰명',
        field: 'couponName',
        flex: 1.5,
        valueFormatter: params => params.value ?? '-',
      },
      {
        headerName: '쿠폰 URL',
        flex: 2.2,
        cellRenderer: (params: any) => {
          const slug = params.data?.linkSlug;
          if (!slug) return '-';
          const url = slug.startsWith('http') ? slug : `https://${slug}`;
          return (
            <div className="!flex !items-center !gap-2 !text-sm !h-full !w-full" style={{ minWidth: 0 }}>
              <span className="!flex-1 !truncate" title={url} style={{ minWidth: 0 }}>
                {url}
              </span>
              <button
                className="!text-blue-600 hover:!text-blue-800"
                onClick={() => copyCouponLink(slug)}
                title="쿠폰 링크 복사"
              >
                <FontAwesomeIcon icon={faLink} />
              </button>
            </div>
          );
        },
      },
      {
        headerName: '가맹점 명',
        field: 'franchiseName',
        flex: 1.8,
        valueFormatter: params => params.value ?? '-',
      },
      {
        headerName: '방문 수',
        field: 'landedUserCount',
        flex: 1,
        valueFormatter: params => params.value?.toLocaleString(),
      },
      {
        headerName: '결과 조회 수',
        field: 'resultUserCount',
        flex: 1,
        valueFormatter: params => params.value?.toLocaleString(),
      },
      {
        headerName: '전환율',
        flex: 1.1,
        valueFormatter: params => {
          const landed = params.data?.landedUserCount ?? 0;
          const result = params.data?.resultUserCount ?? 0;
          if (!landed) {
            return '-';
          }
          const rate = (result / landed) * 100;
          return `${rate.toFixed(1)}%`;
        },
      },
      {
        headerName: '생성일',
        field: 'createdAt',
        flex: 1.5,
        valueFormatter: params => (params.value ? new Date(params.value).toLocaleString() : ''),
      },
      {
        headerName: '입력폼',
        width: 80,
        cellRenderer: (params: any) => {
          const code: IFortuneLabCode | undefined = params.data;
          if (!code?.trkId) return null;
          return (
            <button
              className="!text-blue-600 hover:!text-blue-800"
              onClick={() => handleOpenEmbedModal(code)}
              title="입력폼 퍼가기"
            >
              <FontAwesomeIcon icon={faCode} />
            </button>
          );
        },
      },
      {
        headerName: '관리',
        width: 80,
        cellRenderer: (params: any) => {
          const code: IFortuneLabCode | undefined = params.data;
          if (!code) return null;
          return (
            <button
              className="!text-blue-600 hover:!text-blue-800"
              onClick={() => {
                setSelectedCode(code);
                setEditModalOpen(true);
              }}
              title="캠페인 수정"
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
          );
        },
      },
	    ],
	    [copyCouponLink, copyShareLink, handleOpenEmbedModal]
	  );

	  useEffect(() => {
	    if (!isEmbedModalOpen || !selectedCode) return;

	    const normalizedLandingWidth = (landingWidth || '').trim() || '600';
	    const normalizedButtonWidth = (buttonWidth || '').trim() || '600';

	    if (embedType === 'landing') {
	      setEmbedCodeToCopy(buildLandingEmbedCode(selectedCode.trkId, normalizedLandingWidth, landingOnlyForm));
	    } else {
	      setEmbedCodeToCopy(
	        buildButtonEmbedCode(selectedCode.trkId, normalizedButtonWidth, buttonModalPosition, buttonTypingEnabled)
	      );
	    }
	  }, [
	    isEmbedModalOpen,
	    selectedCode,
	    embedType,
	    landingWidth,
	    landingOnlyForm,
	    buttonWidth,
	    buttonModalPosition,
	    buttonTypingEnabled,
	    buildLandingEmbedCode,
	    buildButtonEmbedCode,
	  ]);

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !items-center !justify-between !py-4">
        <button
          className="!flex !items-center !gap-2 !px-4 !py-2 !rounded !bg-blue-600 !text-white hover:!bg-blue-700"
          onClick={() => setCreateModalOpen(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          새 캠페인 코드 발급
        </button>
      </div>

      <div className="!flex-1 !min-h-0">
        <GridTable<IFortuneLabCode>
          height="100%"
          rowData={codeList}
          columnDefs={columnDefs}
          isLoading={loading}
        />
      </div>

      {codePage && codePage.totalPages > 1 && (
        <div className="!w-full !mt-6">
          <div className="!flex !justify-center !items-center !space-x-2">
            {Array.from({ length: codePage.totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => handlePagination(index)}
                className={`!px-3 !py-1 !rounded !text-sm ${
                  index === codePage.pageable.pageNumber
                    ? '!bg-blue-600 !text-white'
                    : 'hover:!bg-gray-100'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 임베드 코드 표시 모달 */}
      {isEmbedModalOpen && (
        <div className="!fixed !inset-0 !bg-black !bg-opacity-50 !flex !items-center !justify-center !z-50">
          <div className="!bg-white !p-6 !rounded-lg !shadow-xl !w-full !max-w-lg">
            <h3 className="!text-lg !font-bold !mb-4">입력폼 코드 퍼가기</h3>
            <p className="!text-sm !mb-4 !text-gray-500">
              입력폼은 홈페이지나 HTML이 사용가능한 블로그/카페 등에 삽입하여 사용가능합니다. 생성하기 후 복사를 통하여 소스 코드를
              복사하고 삽입할 위치에 추가하시면 됩니다.
            </p>
            <div className="!mb-4">
              <div className="!inline-flex !rounded-md !border !overflow-hidden">
                <button
                  type="button"
                  className={`!px-4 !py-2 !text-sm ${
                    embedType === 'landing'
                      ? '!bg-blue-600 !text-white'
                      : '!bg-white !text-gray-600 hover:!bg-gray-50'
                  }`}
                  onClick={() => setEmbedType('landing')}
                >
                  랜딩 버전
                </button>
                <button
                  type="button"
                  className={`!px-4 !py-2 !text-sm ${
                    embedType === 'button'
                      ? '!bg-blue-600 !text-white'
                      : '!bg-white !text-gray-600 hover:!bg-gray-50'
                  }`}
                  onClick={() => setEmbedType('button')}
                >
                  버튼 버전
                </button>
              </div>
            </div>

            {embedType === 'landing' ? (
              <div className="!mb-4 !space-y-4">
                <div>
                  <div className="!flex !items-center !justify-between !mb-2">
                    <label className="!text-sm !font-medium !text-gray-700">가로폭(px)</label>
                  </div>
                  <div className="!flex !items-center !gap-2">
                    <input
                      type="number"
                      min="200"
                      placeholder="예) 600"
                      className="!flex-1 !p-2 !border !rounded"
                      value={landingWidth}
                      onChange={(event) => setLandingWidth(event.target.value)}
                      onWheel={preventWheelChange}
                    />
                  </div>
                </div>

                <div className="!flex !items-center !gap-2">
                  <input
                    id="landingOnlyFormToggle"
                    type="checkbox"
                    checked={landingOnlyForm}
                    onChange={(event) => setLandingOnlyForm(event.target.checked)}
                  />
                  <label htmlFor="landingOnlyFormToggle" className="!text-sm !text-gray-700">
                    입력폼만 퍼가기
                  </label>
                </div>
              </div>
            ) : (
              <div className="!mb-4 !space-y-4">
                <div>
                  <div className="!flex !items-center !justify-between !mb-2">
                    <label className="!text-sm !font-medium !text-gray-700">가로폭(px)</label>
                  </div>
                  <div className="!flex !items-center !gap-2">
                    <input
                      type="number"
                      min="320"
                      placeholder="예) 600"
                      className="!flex-1 !p-2 !border !rounded"
                      value={buttonWidth}
                      onChange={(event) => setButtonWidth(event.target.value)}
                      onWheel={preventWheelChange}
                    />
                  </div>
                </div>

                <div>
                  <div className="!flex !items-center !justify-between !mb-2">
                    <label className="!text-sm !font-medium !text-gray-700">모달 위치</label>
                    <span className="!text-xs !text-gray-500">버튼은 오른쪽 하단에 고정되어 노출됩니다.</span>
                  </div>
                  <select
                    className="!w-full !p-2 !border !rounded"
                    value={buttonModalPosition}
                    onChange={(event) => setButtonModalPosition(event.target.value as 'left' | 'center' | 'right')}
                  >
                    <option value="left">왼쪽</option>
                    <option value="center">가운데</option>
                    <option value="right">오른쪽</option>
                  </select>
                </div>

                <div className="!flex !items-center !gap-2">
                  <input
                    id="buttonTypingToggle"
                    type="checkbox"
                    checked={buttonTypingEnabled}
                    onChange={(event) => setButtonTypingEnabled(event.target.checked)}
                  />
                  <label htmlFor="buttonTypingToggle" className="!text-sm !text-gray-700">
                    말풍선 타이핑 효과 표시
                  </label>
                </div>
              </div>
            )}

            {embedCodeToCopy ? (
              <>
                <textarea
                  readOnly
                  className="!w-full !h-24 !p-2 !border !rounded !bg-gray-50 !text-sm !resize-none !font-mono"
                  value={embedCodeToCopy}
                />
                <div className="!flex !justify-end !gap-3 !mt-4">
                  <button
                    className="!px-4 !py-2 !rounded !bg-gray-200 hover:!bg-gray-300"
                    onClick={resetEmbedModalState}
                  >
                    닫기
                  </button>
                  <button
                    className="!px-4 !py-2 !rounded !bg-blue-600 !text-white hover:!bg-blue-700"
                    onClick={handleCopyToClipboard}
                  >
                    복사
                  </button>
                </div>
              </>
            ) : (
              <div className="!flex !justify-end !gap-3 !mt-4">
                <button
                  className="!px-4 !py-2 !rounded !bg-gray-200 hover:!bg-gray-300"
                  onClick={resetEmbedModalState}
                >
                  닫기
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <CodeModal
        mode="create"
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
      <CodeModal
        mode="edit"
        isOpen={isEditModalOpen && !!selectedCode}
        code={selectedCode ?? undefined}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedCode(null);
        }}
        onSuccess={async () => {
          const currentPage = codePage?.pageable?.pageNumber ?? 0;
          await fetchCodes(currentPage);
        }}
      />
    </div>
  );
};

export default CodeManagementPage;
